import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { FormEvent } from 'react';

const NAV_LINKS = [
  { label: '首页', to: '/' },
  { label: '热门速览', to: '/hot' },
  { label: '主题分类', to: '/#categories' },
  { label: '关于我们', to: '/about' },
  { label: '联系我们', to: '/contact' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (to: string) => {
    const [path, hash] = to.split('#');
    if (hash) {
      return location.pathname === path && location.hash === `#${hash}`;
    }
    return location.pathname === path && (!location.hash || location.hash === '');
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/hot?q=${encodeURIComponent(q)}` : '/hot');
    setKeyword('');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-xl font-bold text-background-50">
            5
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              5看料网
            </span>
            <span className="mt-1 font-label text-[11px] tracking-[0.24em] text-foreground-500">
              速览 · 快看
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 font-label text-sm transition-colors ${
                isActive(link.to)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={handleSearch}
            className="hidden items-center rounded-md border border-background-300 bg-background-100 px-3 py-2 md:flex"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              placeholder="搜索热点关键词"
              aria-label="搜索热点关键词"
              className="ml-2 w-32 bg-transparent font-body text-sm text-foreground-900 outline-none placeholder:text-foreground-500 xl:w-40"
            />
          </form>

          <Link
            to="/#subscribe"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-block"
          >
            订阅速览
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-2-line text-xl'} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4" aria-label="移动端导航">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-2.5 font-label text-sm ${
                  isActive(link.to)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <form
              onSubmit={handleSearch}
              className="mt-2 flex items-center rounded-md border border-background-300 bg-background-100 px-3 py-2.5"
              role="search"
            >
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                placeholder="搜索热点关键词"
                aria-label="搜索热点关键词"
                className="ml-2 w-full bg-transparent font-body text-sm outline-none placeholder:text-foreground-500"
              />
            </form>
            <Link
              to="/#subscribe"
              className="mt-1 rounded-md bg-primary-500 px-4 py-2.5 text-center font-label text-sm font-medium text-background-50"
            >
              订阅速览
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}